import { createContext } from 'preact';
import { useContext, useEffect, useMemo, useState } from 'preact/hooks';
import { logCommand } from './debug';
import { createStubBridge, createWorkerBridge, type SimBridge, type UiState } from './bridge';

export type TabId = 'reserve' | 'map' | 'herd' | 'park' | 'office';
export type PlayMode = 'ranch' | 'field';
export type PanelId =
  | { kind: 'animal'; id: string }
  | { kind: 'staff' }
  | { kind: 'office'; section: string }
  | { kind: 'photo' }
  | { kind: 'expedition' }
  | { kind: 'observe'; species?: string }
  | { kind: 'finance' }
  | null;

export interface AppStore {
  bridge: SimBridge;
  tab: TabId;
  setTab(t: TabId): void;
  mode: PlayMode;
  setMode(m: PlayMode): void;
  path: string | null;
  /** Begin play: scenario.start with the chosen difficulty, or a P706 catalog scenario id (its start_mode wins sim-side). No path pick (Sim-Core: all six paths open from day one). */
  beginPlay(startMode: string, scenarioId?: string): void;
  /** Begin the P707 campaign at a reserve (story.begin; the reserve's scenario start_mode applies sim-side). Order approved by Matthew 2026-10-23 (Q2 option A). */
  beginCampaign(reserveId: string, startMode: string): void;
  panel: PanelId;
  openPanel(p: NonNullable<PanelId>): void;
  closePanel(): void;
  settingsOpen: boolean;
  setSettingsOpen(b: boolean): void;
  /** Count of currently open local Sheet overlays: floating affordances
   * (the decision pill) hide while any sheet covers the screen. */
  sheetsOpen: number;
  noteSheet(open: boolean): void;
  sim: UiState;
}

const Ctx = createContext<AppStore | null>(null);

export function StoreProvider({ children }: { children: preact.ComponentChildren }) {
  // ?stub=1 renders the shell with stub data (QA/screenshot harness).
  const bridge = useMemo(() => {
    const b = typeof location !== 'undefined' && new URLSearchParams(location.search).has('stub')
      ? createStubBridge() : createWorkerBridge();
    const rawSend = b.send.bind(b);
    b.send = (type, payload) => { logCommand(type, payload); rawSend(type, payload); };
    return b;
  }, []);
  const [tab, setTab] = useState<TabId>('reserve');
  const [mode, setMode] = useState<PlayMode>('ranch');
  const [path, setPath] = useState<string | null>(() => {
    try { const v = localStorage.getItem('ea.path'); return v === 'ranch' ? 'open_ranch' : v; } catch { return null; }
  });
  const beginPlay = (startMode: string, scenarioId?: string) => {
    setPath('begun');
    // mode rides along: today's validator requires it, and the sim ignores it when scenario is set.
    bridge.send('scenario.start', scenarioId === undefined ? { mode: startMode } : { scenario: scenarioId, mode: startMode });
    try {
      localStorage.setItem('ea.path', 'begun');
      localStorage.setItem('ea.mode', startMode);
      if (scenarioId === undefined) localStorage.removeItem('ea.scenario'); else localStorage.setItem('ea.scenario', scenarioId);
    } catch { /* private mode */ }
  };
  const beginCampaign = (reserveId: string, startMode: string) => {
    setPath('begun');
    bridge.send('story.begin', { reserve: reserveId });
    try {
      localStorage.setItem('ea.path', 'begun');
      localStorage.setItem('ea.mode', startMode);
      localStorage.removeItem('ea.scenario');
    } catch { /* private mode */ }
  };
  const [panel, setPanel] = useState<PanelId>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sheetsOpen, setSheetsOpen] = useState(0);
  const noteSheet = (open: boolean) => { setSheetsOpen(n => Math.max(0, n + (open ? 1 : -1))); };
  const [sim, setSim] = useState<UiState>(() => bridge.getState());
  useEffect(() => bridge.subscribe(setSim), [bridge]);
  const store = useMemo<AppStore>(() => ({
    bridge, tab, setTab, mode, setMode, path, beginPlay, beginCampaign, panel,
    openPanel: (p) => { setPanel(p); },
    closePanel: () => { setPanel(null); },
    settingsOpen, setSettingsOpen, sheetsOpen, noteSheet, sim,
  }), [bridge, tab, mode, path, panel, settingsOpen, sheetsOpen, sim]);
  return <Ctx.Provider value={store}>{children}</Ctx.Provider>;
}

export function useStore(): AppStore {
  const s = useContext(Ctx);
  if (!s) throw new Error('store missing');
  return s;
}
